import { defineAsyncComponent } from "vue"

import type { Column, TableConfig } from "@/types"
import { Position, type Employment, Interruption, Area } from "@/graphql/types"
import { i18n } from "@/i18n"
import { type MappedEmployment } from "@/store/employment"
import { useInstitutionStore } from "@/store/institution"
import { getOptions } from "@/utils/composables/useDropdown"
import { formatDate } from "@/utils/date"
import { formatHours, timerangesToString } from "@/utils/misc"

//https://digitale-verwaltung.atlassian.net/wiki/spaces/adebisKITA/pages/1322057729/Basics+Anstellungen+Qualifications+and+qualification+categories
export enum EmploymentCategory {
  /** Fachkraft */
  F = "F",
  /** Ergänzungskraft */
  E = "E",
  /** Zusatzkraft */
  Z = "Z",
  /** Sprachfachkraft (formerly "Schwerpunktkraft") */
  S = "S",
  /** Assistenzkraft */
  A = "A",
  /** opt. Personalbonus */
  P = "P",
  /** Keine */
  N = "N",
}
export enum EmploymentEnum {
  P = "P",
  I = "I",
}
export enum WorkingArea {
  "01" = "01", // 1 Gruppenleitung
  "02" = "02", // 2 Zweit- bzw. Ergänzungskraft
  "03" = "03", // 3 Förderung von Kindern nach SGB VIII/SGB XII
  "04" = "04", // 4 Einrichtungsleitung
  "05" = "05", // 5 Verwaltung
  "09" = "09", // 9 Gruppenübergreifend tätig
}
export enum BeginMode {
  E = "E", // Beginnmonat
  "01" = "01", // Nächster Monat nach Beginn
  "02" = "02", // Übernächster Monat nach Beginn
  B = "B", // Arbeitstage >= Betriebstage / 2
  N = "N", // Nicht im AS zählen
}
export enum EndMode {
  V = "V", // Vormonat
  E = "E", // Endmonat
  "01" = "01", // Ein Monat zusätzlich
  "02" = "02", // 42-Tagefrist anwenden
}
export enum ContractType {
  b = "b", // befristet
  u = "u", // unbefristet
}

export const positionOptions = getOptions(Position, "personal.anstellungen.positionen")
export const categoryOptions = getOptions(
  EmploymentCategory,
  "personal.anstellungen.qualifikationskategorien"
)
export const positionOrInterruptionOptions = getOptions(
  EmploymentEnum,
  "personal.anstellungen.labels"
)
export const interruptionOptions = getOptions(Interruption, "personal.anstellungen.unterbrechungen")
export const workingAreaOptions = getOptions(Area, "arbeitsbereiche")
export const beginModeOptions = getOptions(BeginMode, "beginnmodes")
export const endModeOptions = getOptions(EndMode, "endmodes")
export const contractTypeOptions = getOptions(ContractType, "vertragsarten")

export const config: TableConfig<Employment, MappedEmployment> = {
  columnDefs: [
    {
      key: "n",
      header: i18n.global.t("labels.id"),
    },
    {
      key: "employee.person.lastname",
      header: i18n.global.t("personal.mitarbeiter.felder.nachname.label"),
      requiredPermissions: ["employee:list"],
    },
    {
      key: "employee.person.firstname",
      header: i18n.global.t("personal.mitarbeiter.felder.vorname.label"),
      requiredPermissions: ["employee:list"],
    },
    {
      key: "employee.address.city",
      header: i18n.global.t("personal.mitarbeiter.felder.ort.label"),
      requiredPermissions: ["employee:list"],
    },
    {
      key: "movement.entry",
      format: (employment) => employment.movement?.entry && formatDate(employment.movement?.entry),
      header: i18n.global.t("personal.anstellungen.felder.anstellung_ab.label"),
    },
    {
      key: "movement.exit",
      format: (employment) => employment.movement?.exit && formatDate(employment.movement?.exit),
      header: i18n.global.t("personal.anstellungen.felder.anstellung_bis.label"),
    },
    {
      key: "contractType",
      format: (employment) =>
        employment.contractType && i18n.global.t(`vertragsarten.${employment.contractType}`),
      header: i18n.global.t("personal.anstellungen.felder.art_des_vertrags.label"),
    },
    {
      key: "timesMo",
      fields: ["times.mo.start", "times.mo.end"],
      format: (employment) => employment.times?.mo && timerangesToString(employment.times?.mo),
      header: i18n.global.t("personal.anstellungen.page.labels.mo"),
      active: false,
    },
    {
      key: "timesTu",
      fields: ["times.tu.start", "times.tu.end"],
      format: (employment) => employment.times?.tu && timerangesToString(employment.times?.tu),
      header: i18n.global.t("personal.anstellungen.page.labels.tu"),
      active: false,
    },
    {
      key: "timesWe",
      fields: ["times.we.start", "times.we.end"],
      format: (employment) => employment.times?.we && timerangesToString(employment.times?.we),
      header: i18n.global.t("personal.anstellungen.page.labels.we"),
      active: false,
    },
    {
      key: "timesTh",
      fields: ["times.th.start", "times.th.end"],
      format: (employment) => employment.times?.th && timerangesToString(employment.times?.th),
      header: i18n.global.t("personal.anstellungen.page.labels.th"),
      active: false,
    },
    {
      key: "timesFr",
      fields: ["times.fr.start", "times.fr.end"],
      format: (employment) => employment.times?.fr && timerangesToString(employment.times?.fr),
      header: i18n.global.t("personal.anstellungen.page.labels.fr"),
      active: false,
    },
    {
      key: "f_e",
      fields: ["position"],
      header: i18n.global.t("personal.anstellungen.page.labels.F_E"),
    },
    {
      key: "positionOrInterruption",
      fields: ["position", "interruption"],
      format: (employment) =>
        (employment.position &&
          i18n.global.t(`personal.anstellungen.positionen.${employment.position}`)) ??
        (employment.interruption &&
          i18n.global.t(`personal.anstellungen.unterbrechungen.${employment.interruption}`)),
      header:
        i18n.global.t("personal.anstellungen.felder.anstellung_als.label") +
        " / " +
        i18n.global.t("personal.anstellungen.felder.grund_der_unterbrechung.label"),
    },
    {
      key: "workingHours",
      format: (employment) => formatHours(employment.workingHours),
      header: i18n.global.t("personal.anstellungen.felder.wochenarbeitszeit.label"),
    },
    {
      key: "institutionSection",
      fields: ["institutionSectionWorkingHours.institutionSection.name"],
      requiredPermissions: ["institution:view"],
      format: (employment) =>
        employment.institutionSectionWorkingHours
          ?.map((s) => s.institutionSection?.name)
          .join("\n"),
      header: i18n.global.t("personal.anstellungen.felder.einrichtungsbereiche.label"),
      active: () => {
        const institutionStore = useInstitutionStore()
        return !!institutionStore.contextInstitutionSections?.length
      },
    },
  ],

  filterableColumnDefs: [
    {
      key: "person.firstname", //default
      dataKey: "employee.person.firstname",
      header: i18n.global.t("personal.mitarbeiter.felder.vorname.label"),
      requiredPermissions: ["employee:list"],
      filterType: "string",
    },
    {
      key: "person.lastname", //default
      dataKey: "employee.person.lastname",
      header: i18n.global.t("personal.mitarbeiter.felder.nachname.label"),
      requiredPermissions: ["employee:list"],
      filterType: "string",
    },
    {
      key: "n",
      dataKey: "n",
      header: i18n.global.t("labels.id"),
      filterType: "string",
    },
    {
      key: "employmentType", //default
      dataKey: "employmentType",
      fields: ["interruption"],
      header: i18n.global.t("personal.anstellungen.page.filter.felder.anstell_typ.label"),
      filterType: "select",
      options: positionOrInterruptionOptions,
    },
    {
      key: "employee.address.city",
      dataKey: "employee.address.city",
      header: i18n.global.t("personal.mitarbeiter.felder.ort.label"),
      requiredPermissions: ["employee:list"],
      filterType: "string",
    },
    {
      key: "employee.address.zipCode",
      dataKey: "employee.address.zipCode",
      header: i18n.global.t("personal.mitarbeiter.felder.plz.label"),
      requiredPermissions: ["employee:list"],
      filterType: "string",
    },
    {
      key: "employee.address.district",
      dataKey: "employee.address.district",
      header: i18n.global.t("personal.mitarbeiter.felder.ortsteil.label"),
      requiredPermissions: ["employee:list"],
      filterType: "string",
    },
    {
      key: "position", //default
      dataKey: "position",
      header: i18n.global.t("personal.anstellungen.felder.anstellung_als.label"),
      filterType: "select",
      options: positionOptions,
    },
    {
      key: "contractType", //default
      dataKey: "contractType",
      header: i18n.global.t("personal.anstellungen.felder.art_des_vertrags.label"),
      filterType: "select",
      options: contractTypeOptions,
    },
    {
      key: "category", //default
      dataKey: "category",
      fields: ["position"],
      header: i18n.global.t("personal.anstellungen.page.filter.felder.qualifikation.label"),
      filterType: "select",
      options: categoryOptions,
    },
    {
      key: "movement.entry", //default
      dataKey: "movement.entry",
      header: i18n.global.t("personal.anstellungen.page.filter.felder.eintritt.label"),
      filterType: "date_range",
    },
    {
      key: "movement.exit", //default
      dataKey: "movement.exit",
      header: i18n.global.t("personal.anstellungen.page.filter.felder.austritt.label"),
      filterType: "date_range",
    },
    {
      key: "employeeCategories",
      dataKey: "employeeCategories",
      requiredPermissions: ["category:list"],
      fields: ["employee.categories.category.abbreviation", "employee.categories.category.id"],
      header: i18n.global.t("personal.anstellungen.page.filter.felder.kategorie.label"),
      filterType: "multi_select",
      matchModes: ["contains"],
      component: defineAsyncComponent(
        () => import("@/components/context/CategoriesMultiSelect.vue")
      ),
    },
  ],
}

export const EmployeeEmploymentTableConfig: Column<MappedEmployment, MappedEmployment>[] = [
  {
    key: "movement.entry",
    header: i18n.global.t("personal.anstellungen.felder.anstellung_ab.label"),
    format: (data) => (data.movement?.entry ? formatDate(data.movement?.entry) : ""),
  },
  {
    key: "movement.exit",
    header: i18n.global.t("personal.anstellungen.felder.anstellung_bis.label"),
    format: (data) => (data.movement?.exit ? formatDate(data.movement?.exit) : ""),
  },

  {
    key: "f_e",
    fields: ["position"],
    header: i18n.global.t("personal.anstellungen.page.labels.F_E"),
    format: (data) =>
      data.f_e ? i18n.global.t(`personal.anstellungen.page.labels.${data.f_e}`) : "",
  },

  {
    key: "workingHoursFixed",
    header: i18n.global.t("personal.anstellungen.felder.wochenarbeitszeit.label"),
    format: (data) =>
      data.workingHours === null || data.workingHours === undefined
        ? ""
        : formatHours(data.workingHours),
  },

  {
    key: "position",
    header: i18n.global.t("personal.anstellungen.page.labels.employment_or_reason"),
    format: (data) =>
      data?.interruption
        ? i18n.global.t(`personal.anstellungen.unterbrechungen.${data.interruption}`)
        : i18n.global.t(`personal.anstellungen.positionen.${data.position}`),
  },
]

export default config
